import PropTypes from 'prop-types';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useState } from 'react';

const RelativeDate = ({ date, tooltipPrefix, displayFormat = 'relative' }) => {
  const [open, setOpen] = useState(false);
  const isTouchDevice = typeof window !== 'undefined' && (
    'ontouchstart' in window || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
  );
  
  if (!date) return null;
  
  let dateObj;
  
  // Handle Romanian date format like "17.06.24 9:35" or "17.06.2024 9:35"
  if (typeof date === 'string' && date.match(/^\d{2}\.\d{2}\.\d{2,4}\s+\d{1,2}:\d{2}$/)) {
    const [datePart, timePart] = date.split(' ');
    const [day, month, year] = datePart.split('.');
    const fullYear = year.length === 2 ? '20' + year : year;
    const [hour, minute] = timePart.split(':');
    // Create date assuming Romania timezone
    dateObj = new Date(`${fullYear}-${month}-${day}T${hour.padStart(2, '0')}:${minute}:00+03:00`);
  } else if (typeof date === 'string' && date.match(/^\d{2}\.\d{2}\.\d{2,4}$/)) {
    const [day, month, year] = date.split('.') ;
    const fullYear = year.length === 2 ? '20' + year : year;
    // Create date at midnight in Romania timezone
    dateObj = new Date(`${fullYear}-${month}-${day}T00:00:00+03:00`);
  } else if (typeof date === 'string' && date.includes('T')) {
    // For ISO format dates like "2025-06-26T11:05:06"
    // If no timezone indicator, treat as UTC
    if (!date.endsWith('Z') && !date.match(/[+-]\d{2}:\d{2}$/)) {
      dateObj = new Date(date + 'Z');
    } else {
      dateObj = new Date(date);
    }
  } else {
    // Fallback for other date formats
    dateObj = new Date(date);
  }
  
  const now = new Date();
  const diffMs = now - dateObj;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  // Format functions
  const formatFullDate = (showTime = true) => {
    const options = {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      ...(showTime && {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    };
    return dateObj.toLocaleDateString('ro-RO', options);
  };

  const formatFullDateWithSeconds = () => (
    dateObj.toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  );

  const formatFullRomanianDate = () => (
    dateObj.toLocaleString('ro-RO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  );

  // Removed unused short date formatter to satisfy linter

  // Get relative time text
  const getRelativeText = () => {
    // Detailed relative display (seconds, minutes, hours, days, weeks, months, years)
    if (diffSeconds < 5) return 'acum';
    if (diffSeconds < 60) return ` ${diffSeconds} sec`;
    if (diffMinutes === 1) return ' 1 min';
    if (diffMinutes < 60) return ` ${diffMinutes} min`;
    // Show hours until 3 days (72 hours) to avoid displaying "2 zile"
    if (diffHours < 72) {
      return diffHours === 1 ? ' 1 oră' : ` ${diffHours} ore`;
    }
    // Show number of days up to and including 21 days (3 weeks)
    if (diffDays <= 21) {
      return diffDays === 1 ? ' 1 zi' : ` ${diffDays} zile`;
    }
    // After 21 days and before 30 days, show weeks
    if (diffDays < 30) {
      return diffWeeks === 1 ? ' 1 săpt' : `${diffWeeks} săpt`;
    }
    if (diffMonths === 1) return '1 lună';
    if (diffMonths < 12) return `${diffMonths} luni`;
    // 360–364 days: diffMonths is already 12 but diffYears is still 0
    if (diffYears <= 1) return '1 an';
    return `${diffYears} ani`;
  };

  const relativeText = getRelativeText();
  const relativeTextTrimmed = relativeText.trim();
  const showTimeInHover = true;

  const userPrefersFullDate = typeof window !== 'undefined'
    && localStorage.getItem('date_format_preference') === 'full';

  const tooltipBaseText = userPrefersFullDate
    ? relativeTextTrimmed
    : (displayFormat === 'fullWithRelative' || displayFormat === 'fullRomanian'
        ? formatFullDateWithSeconds()
        : formatFullDate(showTimeInHover));
  const tooltipText = tooltipPrefix
    ? `${tooltipPrefix}: ${tooltipBaseText}`
    : tooltipBaseText;

  const displayText = userPrefersFullDate
    ? formatFullRomanianDate()
    : (displayFormat === 'fullWithRelative'
        ? `${formatFullDateWithSeconds()} (${relativeTextTrimmed})`
        : displayFormat === 'fullRomanian'
          ? formatFullRomanianDate()
          : relativeText);

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={300}
        open={isTouchDevice ? open : undefined}
        onOpenChange={isTouchDevice ? setOpen : undefined}
      >
        <TooltipTrigger asChild>
          <span
            className="cursor-default"
            onClick={isTouchDevice ? () => setOpen(prev => !prev) : undefined}
          >
            {displayText}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

RelativeDate.propTypes = {
  date: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.number
  ]),
  tooltipPrefix: PropTypes.string,
  displayFormat: PropTypes.oneOf(['relative', 'fullWithRelative', 'fullRomanian'])
};

export default RelativeDate; 
